import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import type { PaymentPeriod } from "../../api/pricelist";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import type { TariffPlanPriceOption, TariffPlanView } from "../../utils/tariffPlans";
import {
  MSG_FREE_MONTH,
  MSG_FREE_ONCE,
  formatTariffChecksCount,
} from "../../utils/tariffPlans";
import { formatPricelistPrice } from "../../utils/pricelistFormat";
import "./TariffPlanCard.scss";

const PERIOD_LABELS: Record<PaymentPeriod, string> = {
  month: "Месяц",
  quarter: "Квартал (3 месяца)",
  year: "Год",
};

export interface BuySelection {
  period: PaymentPeriod;
  amount: number;
}

interface TariffPlanCardProps {
  plan: TariffPlanView;
  requesting?: boolean;
  freeUsed?: boolean;
  pendingLabel?: string | null;
  onRequest: (selection: BuySelection) => void;
}

export const TariffPlanCard = ({
  plan,
  requesting,
  freeUsed = false,
  pendingLabel = null,
  onRequest,
}: TariffPlanCardProps) => {
  const [selectedKey, setSelectedKey] = useState<PaymentPeriod>(plan.defaultPeriod);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedKey(plan.defaultPeriod);
    setError(null);
  }, [plan.defaultPeriod, plan.id]);

  useEffect(() => {
    if (plan.isZeroTariff && selectedKey !== "month") {
      const monthly = plan.prices.find((price) => price.key === "month");
      if (monthly) setSelectedKey("month");
    }
  }, [plan.isZeroTariff, plan.prices, selectedKey]);

  const selectedPrice: TariffPlanPriceOption | undefined =
    plan.prices.find((price) => price.key === selectedKey) ??
    plan.prices.find((price) => price.isPrimary) ??
    plan.prices[0];

  const showPeriods = !plan.isZeroTariff && plan.prices.length > 0;
  const freeLocked = plan.isZeroTariff && freeUsed;
  const hasPending = Boolean(pendingLabel);

  const handleBuy = () => {
    if (hasPending) return;
    if (freeLocked) {
      setError(MSG_FREE_ONCE);
      return;
    }

    if (plan.isZeroTariff && selectedKey !== "month") {
      setError(MSG_FREE_MONTH);
      return;
    }

    if (!selectedPrice) {
      setError("Выберите период оплаты");
      return;
    }

    setError(null);
    onRequest({
      period: selectedPrice.period,
      amount: selectedPrice.buyAmount,
    });
  };

  return (
    <article className="tariff-plan-card">
      {(plan.title || plan.description) && (
        <div className="tariff-plan-card-top">
          {plan.title && <h2 className="tariff-plan-card-title">{plan.title}</h2>}
          {plan.description && <p className="tariff-plan-card-desc">{plan.description}</p>}
        </div>
      )}

      {selectedPrice && (
        <div className="tariff-plan-card-prices">
          <div className="tariff-plan-card-price-main">
            <span className="tariff-plan-card-amount">
              {formatPricelistPrice(selectedPrice.price)}
            </span>
            <span className="tariff-plan-card-period">
              {PERIOD_LABELS[selectedPrice.key] ?? selectedPrice.periodLabel}
            </span>
          </div>

          {showPeriods ? (
            <div className="tariff-plan-card-period-options" role="group" aria-label="Период оплаты">
              {plan.prices.map((price) => (
                <button
                  key={price.key}
                  type="button"
                  className={`tariff-plan-card-period-btn${
                    price.key === selectedKey ? " tariff-plan-card-period-btn--active" : ""
                  }`}
                  disabled={requesting}
                  onClick={() => {
                    setSelectedKey(price.key);
                    setError(null);
                  }}
                >
                  {PERIOD_LABELS[price.key] ?? price.periodLabel}
                  {` · ${formatPricelistPrice(price.price)}`}
                </button>
              ))}
            </div>
          ) : null}

          {plan.isZeroTariff ? (
            <p className="tariff-plan-card-price-hint">{MSG_FREE_MONTH}</p>
          ) : showPeriods ? (
            <p className="tariff-plan-card-price-hint">
              Выберите период: месяц, квартал (3 месяца) или год
            </p>
          ) : null}
        </div>
      )}

      {plan.checkTypes.length > 0 && (
        <div className="tariff-plan-card-check-types">
          <p className="tariff-plan-card-checks-label">Типы проверок</p>
          <div className="tariff-plan-card-check-type-list">
            {plan.checkTypes.map((checkType) => {
              const options: string[] = [];
              if (checkType.schedule) options.push("расписание");
              if (checkType.report) options.push("отчёт на почту");

              return (
                <div key={checkType.key} className="tariff-plan-card-check-type">
                  <div className="tariff-plan-card-check-type-head">
                    <p className="tariff-plan-card-check-type-name">{checkType.label}</p>
                    <p className="tariff-plan-card-check-type-limit">
                      {formatTariffChecksCount(checkType.baseLimit)}
                    </p>
                  </div>
                  {checkType.categoryNames.length > 0 ? (
                    <p className="tariff-plan-card-check-type-categories">
                      {checkType.categoryNames.join(", ")}
                    </p>
                  ) : null}
                  {options.length > 0 ? (
                    <ul className="tariff-plan-card-checks-list">
                      {options.map((option) => (
                        <li key={option} className="tariff-plan-card-check-item">
                          <span className="tariff-plan-card-check-icon" aria-hidden>
                            <Check size={14} strokeWidth={2} />
                          </span>
                          <span>{option}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {freeLocked ? <p className="tariff-plan-card-warning">{MSG_FREE_ONCE}</p> : null}
      {error ? <p className="tariff-plan-card-warning">{error}</p> : null}

      <div className="tariff-plan-card-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={requesting || freeLocked || hasPending || !selectedPrice}
          onClick={handleBuy}
        >
          {requesting ? (
            <CircularProgress size="sm" />
          ) : pendingLabel ? (
            pendingLabel
          ) : plan.isZeroTariff ? (
            "Подключить бесплатно"
          ) : (
            "Купить тариф"
          )}
        </button>
      </div>
    </article>
  );
};
